import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVideoCall } from '../../context/VideoCallContext';
import { useAppDispatch } from '../../hooks/redux';
import { setCurrentConversation } from '../../store/reducers/messages.reducer';
import { getActiveMessagingInstance } from '../../services/messaging/providers/ServerMessagingProvider';

// Minimized bubble dimensions. On MOBILE (viewport < 768px) the bubble is a
// full-width banner pinned to the top, ~40% of viewport height — easier to see
// while still leaving room to read the chat below. On desktop/tablet it stays
// the original small floating pill so it doesn't dominate the window.
const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 768;
const getMiniW = () => (isMobileViewport() ? window.innerWidth : 150);
const getMiniH = () => (isMobileViewport() ? Math.round(window.innerHeight * 0.4) : 110);
// Defaults used only for first render before getMini*() can run reliably.
const MINI_W = typeof window !== 'undefined' ? getMiniW() : 150;
const MINI_H = typeof window !== 'undefined' ? getMiniH() : 110;
// Local PiP in full-screen view
const PIP_W = 180;
const PIP_H = 130;
// How much of the PiP stays visible when snapped to an edge — 30% on-screen
const PIP_PEEK = Math.round(PIP_W * 0.30);

const HangUpIcon = ({ size = 26 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"
      transform="rotate(135,12,12)"/>
  </svg>
);

// ── Ringtone via Web Audio API ─────────────────────────────────────────────
function useRingtone(active: boolean, muted: boolean) {
  const ctxRef  = useRef<AudioContext | null>(null);
  const stopRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!active || muted) {
      stopRef.current?.();
      stopRef.current = null;
      return;
    }

    let ctx: AudioContext;
    try { ctx = new AudioContext(); }
    catch { return; } // browser blocked audio — skip ringtone, don't crash

    ctxRef.current = ctx;
    let cancelled = false;

    const playRing = () => {
      if (cancelled || ctx.state === 'closed') return;
      try {
        const dur = 1.0;
        [480, 440].forEach(freq => {
          const osc  = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.18, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + dur);
          osc.connect(gain).connect(ctx.destination);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + dur);
        });
      } catch { return; } // audio context may have been suspended
      const id = window.setTimeout(playRing, 3000);
      stopRef.current = () => { window.clearTimeout(id); cancelled = true; try { ctx.close(); } catch {} };
    };

    // Resume context if suspended (Android requires user-gesture unlock)
    (ctx.state === 'suspended' ? ctx.resume() : Promise.resolve())
      .then(playRing)
      .catch(() => {});

    return () => { stopRef.current?.(); stopRef.current = null; cancelled = true; };
  }, [active, muted]);
}

export const VideoCallWidget: React.FC = () => {
  const {
    callState, endReason, incomingCaller, remoteStream, localStream,
    acceptCall, rejectCall, hangUp, switchCamera, callError, callWarning,
    audioOutputDevices, selectedAudioOutput, setAudioOutputDevice, audioOutputSupported
  } = useVideoCall();

  const [audioMenuOpen, setAudioMenuOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [minimized, setMinimized] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  // Missed-messages counter — increments on every `relayed-message` arrival
  // while the call is connected and the user can't see the chat (fullscreen).
  // Resets when the widget is minimized (chat now visible behind) or when the
  // call ends.
  const [missedCount, setMissedCount] = useState(0);

  // `accepted` must be STATE (not a ref) so flipping it triggers a re-render —
  // otherwise the ringtone keeps playing for the several seconds between Accept
  // tap and peer.on('stream') firing the state change to 'connected'.
  const [accepted, setAccepted] = useState(false);
  useEffect(() => { if (callState !== 'ringing') setAccepted(false); }, [callState]);
  const handleAccept = () => { setAccepted(true); acceptCall(); };

  // Ringer respects the app's notification toggle (localStorage 'notificationsDisabled')
  const ringerMuted = localStorage.getItem('notificationsDisabled') === 'true';
  useRingtone(callState === 'ringing' && !accepted, ringerMuted);

  // Navigate to the caller's chat when a call arrives
  useEffect(() => {
    if (callState === 'ringing' && incomingCaller?.userId) {
      navigate('/dashboard/messages');
      dispatch(setCurrentConversation(incomingCaller.userId));
    }
  }, [callState, incomingCaller, navigate, dispatch]);

  // Local PiP dimensions — adapt to actual camera orientation
  const [pipDims, setPipDims] = useState({ w: PIP_W, h: PIP_H });
  const pipDimsRef = useRef({ w: PIP_W, h: PIP_H });
  useEffect(() => {
    const video = localVideoElRef.current;
    if (!video || !localStream) { setPipDims({ w: PIP_W, h: PIP_H }); return; }
    const onMeta = () => {
      const { videoWidth: vw, videoHeight: vh } = video;
      if (!vw || !vh) return;
      const aspect = vw / vh;
      if (vh > vw) {
        const w = Math.round(PIP_H * aspect);
        const dims = { w: Math.max(w, 75), h: PIP_H };
        setPipDims(dims); pipDimsRef.current = dims;
      } else {
        const dims = { w: PIP_W, h: PIP_H };
        setPipDims(dims); pipDimsRef.current = dims;
      }
    };
    video.addEventListener('loadedmetadata', onMeta);
    if (video.readyState >= 1) onMeta();
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, [localStream]);

  // Minimized bubble dimensions. On mobile the bubble is a fixed full-width
  // banner — we skip aspect-ratio adaptation since the size is dictated by
  // the viewport, not the remote camera. On desktop it adapts to remote shape.
  useEffect(() => {
    if (isMobileViewport()) { setBubbleSize({ w: getMiniW(), h: getMiniH() }); return; }
    const video = miniRemoteElRef.current;
    if (!video || !remoteStream) { setBubbleSize({ w: MINI_W, h: MINI_H }); return; }
    const onMeta = () => {
      const { videoWidth: vw, videoHeight: vh } = video;
      if (!vw || !vh) return;
      const aspect = vw / vh;
      if (vh > vw) {
        // Portrait remote — make bubble taller than wide
        const w = Math.round(MINI_H * aspect);
        setBubbleSize({ w: Math.max(w, 60), h: MINI_H });
      } else {
        setBubbleSize({ w: MINI_W, h: MINI_H });
      }
    };
    video.addEventListener('loadedmetadata', onMeta);
    if (video.readyState >= 1) onMeta();
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, [remoteStream]);

  // Minimized bubble drag. Mobile starts flush at top (full-width banner);
  // desktop starts top-center as a small floating pill.
  const [bubblePos, setBubblePos] = useState(() =>
    isMobileViewport() ? { x: 0, y: 0 } : { x: Math.round((window.innerWidth - MINI_W) / 2), y: 16 }
  );
  const [bubbleSize, setBubbleSize] = useState({ w: MINI_W, h: MINI_H });
  const bubbleDragging = useRef(false);
  const bubbleMoved   = useRef(false);
  const bubbleDragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });
  // 'none' | 'drag' | 'pinch' — only one gesture active at a time on the bubble
  const bubbleGesture = useRef<'none' | 'drag' | 'pinch'>('none');

  // Local PiP drag (fullscreen view) — starts top-center so the self-preview
  // doesn't obscure remote-video controls or hide behind nav bars on mobile.
  const [pipPos, setPipPos] = useState({
    x: Math.round((window.innerWidth - PIP_W) / 2),
    y: 16,
  });
  const pipDragging = useRef(false);
  const pipDragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 });
  const pipGesture   = useRef<'none' | 'drag' | 'pinch'>('none');

  // Pinch-zoom refs — use refs (not state) to avoid re-renders during gesture
  const pipZoomRef    = useRef(1);
  const pipPinchRef   = useRef<{ dist: number; zoom: number } | null>(null);
  const remZoomRef    = useRef(1);
  const remPinchRef   = useRef<{ dist: number; zoom: number } | null>(null);
  const bubZoomRef    = useRef(1);
  const bubPinchRef   = useRef<{ dist: number; zoom: number } | null>(null);

  // Count messages that arrive over the socket during a call so the user can
  // see how many they've missed (WhatsApp-style). Subscribe for the whole
  // duration of the call so the count is correct even if the widget is
  // minimized & re-expanded.
  useEffect(() => {
    if (callState !== 'connected') return;
    let socket = getActiveMessagingInstance()?.getSocket();
    // The messaging socket might not be ready the millisecond the call connects
    // (e.g. fresh PWA boot). Poll briefly until it shows up.
    let attached = false;
    const onRelayed = () => setMissedCount(c => c + 1);
    const attach = () => {
      socket = getActiveMessagingInstance()?.getSocket();
      if (socket && !attached) {
        socket.on('relayed-message', onRelayed);
        attached = true;
      }
    };
    attach();
    const poll = attached ? null : setInterval(attach, 500);
    return () => {
      if (poll) clearInterval(poll);
      if (socket && attached) socket.off('relayed-message', onRelayed);
    };
  }, [callState]);

  // Reset the counter when the call ends so a new call starts fresh.
  useEffect(() => {
    if (callState !== 'connected') setMissedCount(0);
  }, [callState]);

  useEffect(() => {
    if (callState === 'connected') {
      setMinimized(false);
      setMuted(false);
      setCameraOff(false);
      setBubblePos(isMobileViewport()
        ? { x: 0, y: 0 }
        : { x: Math.round((window.innerWidth - MINI_W) / 2), y: 16 }
      );
      setBubbleSize({ w: getMiniW(), h: getMiniH() });
      bubZoomRef.current = 1;
      pipZoomRef.current = 1;
      remZoomRef.current = 1;
      setPipPos({ x: Math.round((window.innerWidth - PIP_W) / 2), y: 16 });
    }
  }, [callState]);

  // Callback refs MUST be stable identity (useCallback with no deps that reads
  // from the latest stream via a ref) — otherwise React reinvokes them every
  // render, reassigning srcObject and causing constant video reload/flicker on
  // Android. Stream changes are handled by a single useEffect below.
  const remoteVideoElRef = useRef<HTMLVideoElement | null>(null);
  const localVideoElRef  = useRef<HTMLVideoElement | null>(null);
  const miniRemoteElRef  = useRef<HTMLVideoElement | null>(null);
  const miniLocalElRef   = useRef<HTMLVideoElement | null>(null);

  // Keep latest streams in a ref so the stable callback refs can read them
  // without changing identity when the stream value changes.
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localStreamRef  = useRef<MediaStream | null>(null);
  remoteStreamRef.current = remoteStream;
  localStreamRef.current  = localStream;

  // Apply the chosen audio output (speaker/headset) to a remote-stream element.
  // setSinkId() is unsupported on Safari/iOS — silently no-op there.
  const applySinkId = (el: HTMLVideoElement | null) => {
    if (!el || !selectedAudioOutput) return;
    const anyEl = el as any;
    if (typeof anyEl.setSinkId === 'function') {
      anyEl.setSinkId(selectedAudioOutput).catch(() => {});
    }
  };

  const setRemoteEl = useCallback((el: HTMLVideoElement | null) => {
    remoteVideoElRef.current = el;
    if (el && el.srcObject !== remoteStreamRef.current) el.srcObject = remoteStreamRef.current;
    applySinkId(el);
  }, [selectedAudioOutput]); // eslint-disable-line react-hooks/exhaustive-deps
  const setLocalEl = useCallback((el: HTMLVideoElement | null) => {
    localVideoElRef.current = el;
    if (el && el.srcObject !== localStreamRef.current) el.srcObject = localStreamRef.current;
  }, []);
  const setMiniRemoteEl = useCallback((el: HTMLVideoElement | null) => {
    miniRemoteElRef.current = el;
    if (el && el.srcObject !== remoteStreamRef.current) el.srcObject = remoteStreamRef.current;
    applySinkId(el);
  }, [selectedAudioOutput]); // eslint-disable-line react-hooks/exhaustive-deps
  const setMiniLocalEl = useCallback((el: HTMLVideoElement | null) => {
    miniLocalElRef.current = el;
    if (el && el.srcObject !== localStreamRef.current) el.srcObject = localStreamRef.current;
  }, []);

  // Re-apply sink to already-mounted remote elements when the selected device changes.
  useEffect(() => {
    applySinkId(remoteVideoElRef.current);
    applySinkId(miniRemoteElRef.current);
  }, [selectedAudioOutput]);

  // Push the stream onto any already-mounted elements when the stream identity
  // actually changes. The `!==` guard prevents reassigning the same stream
  // (which also triggers a video reload on some browsers).
  useEffect(() => {
    [remoteVideoElRef.current, miniRemoteElRef.current].forEach(el => {
      if (el && el.srcObject !== remoteStream) el.srcObject = remoteStream;
    });
  }, [remoteStream]);
  useEffect(() => {
    [localVideoElRef.current, miniLocalElRef.current].forEach(el => {
      if (el && el.srcObject !== localStream) el.srcObject = localStream;
    });
  }, [localStream]);

  useEffect(() => { localStream?.getAudioTracks().forEach(t => { t.enabled = !muted; }); }, [muted, localStream]);
  useEffect(() => { localStream?.getVideoTracks().forEach(t => { t.enabled = !cameraOff; }); }, [cameraOff, localStream]);

  // Snap the PiP to an edge — uses current pipDimsRef so portrait cameras snap correctly
  const snapPip = (x: number, y: number) => {
    const { w: pw, h: ph } = pipDimsRef.current;
    const peek = Math.round(pw * 0.30); // 30% visible when parked
    const W = window.innerWidth;
    const H = window.innerHeight;
    let nx = x, ny = Math.max(0, Math.min(y, H - ph));
    if (x + pw / 2 < W * 0.30) {
      nx = -pw + peek;
    } else if (x + pw / 2 > W * 0.70) {
      nx = W - peek;
    } else {
      nx = Math.max(-pw + peek, Math.min(x, W - peek));
    }
    setPipPos({ x: nx, y: ny });
  };

  useEffect(() => {
    const getXY = (e: MouseEvent | TouchEvent) => 'touches' in e
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };

    const onMove = (e: MouseEvent | TouchEvent) => {
      const { x, y } = getXY(e);
      if (bubbleDragging.current && bubbleGesture.current !== 'pinch') {
        bubbleMoved.current = true;
        const nx = bubbleDragStart.current.ox + (x - bubbleDragStart.current.mx);
        const ny = bubbleDragStart.current.oy + (y - bubbleDragStart.current.my);
        setBubblePos({
          x: Math.max(0, Math.min(nx, window.innerWidth  - MINI_W)),
          y: Math.max(0, Math.min(ny, window.innerHeight - MINI_H)),
        });
      }
      if (pipDragging.current && pipGesture.current !== 'pinch') {
        const { w: pw, h: ph } = pipDimsRef.current;
        const peek = Math.round(pw * 0.30);
        const nx = pipDragStart.current.ox + (x - pipDragStart.current.mx);
        const ny = pipDragStart.current.oy + (y - pipDragStart.current.my);
        setPipPos({
          x: Math.max(-(pw - peek), Math.min(nx, window.innerWidth  - peek)),
          y: Math.max(0,            Math.min(ny, window.innerHeight - ph)),
        });
      }
    };

    const onUp = () => {
      if (pipDragging.current) {
        setPipPos(prev => {
          const { w: pw } = pipDimsRef.current;
          const peek = Math.round(pw * 0.30);
          const W = window.innerWidth;
          const snapped = { ...prev };
          if (prev.x + pw / 2 < W * 0.30)     snapped.x = -pw + peek;
          else if (prev.x + pw / 2 > W * 0.70) snapped.x = W - peek;
          return snapped;
        });
      }
      bubbleDragging.current = false;
      pipDragging.current    = false;
      if (pipGesture.current === 'drag') pipGesture.current = 'none';
      if (bubbleGesture.current === 'drag') bubbleGesture.current = 'none';
    };

    window.addEventListener('mousemove',  onMove);
    window.addEventListener('mouseup',    onUp);
    window.addEventListener('touchmove',  onMove, { passive: true });
    window.addEventListener('touchend',   onUp);
    return () => {
      window.removeEventListener('mousemove',  onMove);
      window.removeEventListener('mouseup',    onUp);
      window.removeEventListener('touchmove',  onMove);
      window.removeEventListener('touchend',   onUp);
    };
  }, []);

  const onBubbleDown = (e: React.MouseEvent | React.TouchEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    bubbleDragging.current = true;
    bubbleMoved.current    = false;
    bubbleDragStart.current = { mx: x, my: y, ox: bubblePos.x, oy: bubblePos.y };
    e.stopPropagation();
  };

  const onPipDown = (e: React.MouseEvent | React.TouchEvent) => {
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    pipDragging.current = true;
    pipDragStart.current = { mx: x, my: y, ox: pipPos.x, oy: pipPos.y };
    e.stopPropagation();
    e.preventDefault();
  };

  // ── Incoming call ──────────────────────────────────────────────────────────
  if (callState === 'ringing' && incomingCaller) {
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <div style={S.avatar}>
            {incomingCaller.avatar
              ? <img src={incomingCaller.avatar} alt="" style={S.avatarImg} />
              : <span style={S.avatarInitial}>{incomingCaller.name[0]?.toUpperCase()}</span>}
          </div>
          <p style={S.callerName}>{incomingCaller.name}</p>
          <p style={S.callerSub}>Incoming video call…</p>
          <div style={S.callActions}>
            <button onClick={rejectCall} style={S.answerBtnRed}  aria-label="Decline"><HangUpIcon /></button>
            <button onClick={handleAccept} style={S.answerBtnGreen} aria-label="Accept">
              <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Outgoing / waiting ─────────────────────────────────────────────────────
  if (callState === 'calling') {
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <p style={S.callerName}>Calling…</p>
          <p style={S.callerSub}>Waiting for the other person to pick up</p>
          <button onClick={hangUp} style={{ ...S.answerBtnRed, marginTop: 20 }} aria-label="Cancel"><HangUpIcon /></button>
        </div>
      </div>
    );
  }

  // ── Ended overlay ──────────────────────────────────────────────────────────
  if (callState === 'ended') {
    const msgs: Record<string, string> = {
      rejected: 'Call declined', busy: 'User is on another call',
      unavailable: 'User is not available', error: 'Call failed',
      hangup: 'Call ended',
    };
    return (
      <div style={S.overlay}>
        <div style={S.incomingCard}>
          <p style={S.callerName}>{msgs[endReason ?? 'hangup'] ?? 'Call ended'}</p>
          {endReason === 'error' && callError && (
            <p style={S.callerSub}>{callError}</p>
          )}
        </div>
      </div>
    );
  }

  if (callState !== 'connected') return null;

  // ── Minimized bubble — shows BOTH streams ──────────────────────────────────
  if (minimized) {
    return (
      <div
        style={{ ...S.miniBubble, width: bubbleSize.w, height: bubbleSize.h, left: bubblePos.x, top: bubblePos.y }}
        onMouseDown={onBubbleDown}
        onTouchStart={(e) => {
          if (e.touches.length >= 2) {
            // Second finger arrived — cancel any drag in progress and switch to pinch
            bubbleDragging.current = false;
            bubbleGesture.current = 'pinch';
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            bubPinchRef.current = { dist: Math.hypot(dx, dy), zoom: bubZoomRef.current };
          } else if (bubbleGesture.current === 'none') {
            bubbleGesture.current = 'drag';
            onBubbleDown(e);
          }
        }}
        onTouchMove={(e) => {
          if (bubbleGesture.current === 'pinch' && e.touches.length >= 2 && bubPinchRef.current) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            bubZoomRef.current = Math.min(3, Math.max(0.5, bubPinchRef.current.zoom * dist / bubPinchRef.current.dist));
            setBubbleSize({ w: Math.round(MINI_W * bubZoomRef.current), h: Math.round(MINI_H * bubZoomRef.current) });
          }
          // drag is handled by the global window touchmove listener
        }}
        onTouchEnd={(e) => {
          if (e.touches.length === 0) {
            const wasTap = !bubbleMoved.current && bubbleGesture.current === 'drag';
            bubPinchRef.current = null;
            bubbleDragging.current = false;
            bubbleGesture.current = 'none';
            if (wasTap) setMinimized(false);
          } else if (e.touches.length === 1 && bubbleGesture.current === 'pinch') {
            // Last pinch finger lifted — reset to allow a fresh drag
            bubPinchRef.current = null;
            bubbleGesture.current = 'none';
            bubbleDragging.current = false;
          }
        }}
        onMouseUp={() => { if (!bubbleMoved.current) setMinimized(false); }}
        title="Tap to expand • Pinch to resize"
      >
        {/* Remote fills bubble */}
        <video ref={setMiniRemoteEl} autoPlay playsInline style={S.miniRemote} />
        {/* Local small overlay — bottom-right */}
        <video ref={setMiniLocalEl} autoPlay playsInline muted style={S.miniLocal} />
        {/* Hang-up button */}
        <button
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); hangUp(); }}
          style={S.miniHangup}
          aria-label="End call"
        >
          <HangUpIcon size={12} />
        </button>
        {/* Missed-message badge on minimized bubble */}
        {missedCount > 0 && (
          <div
            style={S.miniMissedBadge}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); setMissedCount(0); }}
            title={`${missedCount} new message${missedCount === 1 ? '' : 's'}`}
          >
            {missedCount > 99 ? '99+' : missedCount}
          </div>
        )}
      </div>
    );
  }

  // ── Full-screen connected call ─────────────────────────────────────────────
  return (
    <div style={S.fullscreen}>
      <style>{`@keyframes missedPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }`}</style>
      {callWarning && (
        <div style={S.warningBanner}>{callWarning}</div>
      )}
      <video
        ref={setRemoteEl} autoPlay playsInline style={S.remoteVideo}
        onTouchStart={(e) => {
          if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            remPinchRef.current = { dist: Math.hypot(dx, dy), zoom: remZoomRef.current };
          }
        }}
        onTouchMove={(e) => {
          if (e.touches.length === 2 && remPinchRef.current && remoteVideoElRef.current) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            remZoomRef.current = Math.min(4, Math.max(1, remPinchRef.current.zoom * dist / remPinchRef.current.dist));
            remoteVideoElRef.current.style.transform = `scale(${remZoomRef.current})`;
          }
        }}
        onTouchEnd={() => { remPinchRef.current = null; }}
        onDoubleClick={() => {
          remZoomRef.current = 1;
          if (remoteVideoElRef.current) remoteVideoElRef.current.style.transform = 'scale(1)';
        }}
      />

      {/* Local PiP — slides to edge, pinch to zoom, drag to move */}
      <video
        ref={setLocalEl}
        autoPlay playsInline muted
        onMouseDown={onPipDown}
        onTouchStart={(e) => {
          if (e.touches.length >= 2) {
            // Cancel any drag and switch to pinch
            pipDragging.current = false;
            pipGesture.current = 'pinch';
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            pipPinchRef.current = { dist: Math.hypot(dx, dy), zoom: pipZoomRef.current };
          } else if (pipGesture.current === 'none') {
            pipGesture.current = 'drag';
            onPipDown(e);
          }
        }}
        onTouchMove={(e) => {
          if (pipGesture.current === 'pinch' && e.touches.length >= 2 && pipPinchRef.current && localVideoElRef.current) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            pipZoomRef.current = Math.min(4, Math.max(0.5, pipPinchRef.current.zoom * dist / pipPinchRef.current.dist));
            localVideoElRef.current.style.transform = `scale(${pipZoomRef.current})`;
          }
        }}
        onTouchEnd={(e) => {
          if (e.touches.length === 0) {
            pipPinchRef.current = null;
            pipDragging.current = false;
            pipGesture.current = 'none';
          } else if (e.touches.length === 1 && pipGesture.current === 'pinch') {
            // Last pinch finger lifted — allow fresh drag
            pipPinchRef.current = null;
            pipDragging.current = false;
            pipGesture.current = 'none';
          }
        }}
        onDoubleClick={() => {
          pipZoomRef.current = 1;
          if (localVideoElRef.current) localVideoElRef.current.style.transform = 'scale(1)';
        }}
        style={{ ...S.localVideo, width: pipDims.w, height: pipDims.h, left: pipPos.x, top: pipPos.y, cursor: 'grab' }}
        title="Drag to tuck away • Pinch to zoom • Double-tap to reset"
      />

      <div style={S.topBar}>
        <span style={S.callLabel}>🔒 Encrypted Call</span>
        {missedCount > 0 && (
          <button
            onClick={() => { setMissedCount(0); setMinimized(true); }}
            style={S.missedBadge}
            title={`${missedCount} new message${missedCount === 1 ? '' : 's'} — tap to view`}
            aria-label={`${missedCount} new messages`}
          >
            <span className="material-icons" style={{ fontSize: 16 }}>chat</span>
            <span>{missedCount > 99 ? '99+' : missedCount}</span>
          </button>
        )}
        <button onClick={() => setMinimized(true)} style={S.topBtn} title="Minimize">
          <svg viewBox="0 0 24 24" fill="white" width="20" height="20">
            <path d="M20 14H4v-2h16v2z"/>
          </svg>
        </button>
      </div>

      <div style={S.controls}>
        {audioOutputSupported && audioOutputDevices.length > 1 && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setAudioMenuOpen(o => !o)}
              style={{ ...S.ctrlBtn, background: 'rgba(255,255,255,0.2)' }}
              title="Audio output"
            >
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            </button>
            {audioMenuOpen && (
              <div style={S.audioMenu}>
                {audioOutputDevices.map(d => (
                  <button
                    key={d.deviceId}
                    onClick={() => { setAudioOutputDevice(d.deviceId); setAudioMenuOpen(false); }}
                    style={{
                      ...S.audioMenuItem,
                      fontWeight: (selectedAudioOutput === d.deviceId || (!selectedAudioOutput && d.deviceId === 'default')) ? 700 : 400
                    }}
                  >
                    {d.label || `Speaker ${d.deviceId.slice(0, 6)}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {'ontouchstart' in window && (
          <button onClick={switchCamera} style={S.ctrlBtn} title="Flip camera">
            <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
              <path d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-5 11.5V14H9v2.5L5.5 13 9 9.5V12h6V9.5l3.5 3.5-3.5 3.5z"/>
            </svg>
          </button>
        )}
        <button
          onClick={() => setMuted(m => !m)}
          style={{ ...S.ctrlBtn, background: muted ? '#ff3b30' : 'rgba(255,255,255,0.2)' }}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted
            ? <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>
            : <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"/></svg>
          }
        </button>
        <button
          onClick={() => setCameraOff(c => !c)}
          style={{ ...S.ctrlBtn, background: cameraOff ? '#ff3b30' : 'rgba(255,255,255,0.2)' }}
          title={cameraOff ? 'Camera on' : 'Camera off'}
        >
          {cameraOff
            ? <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M21 6.5l-4-4-15 15 1.41 1.41 2.39-2.38A2 2 0 0 0 6 19h12a2 2 0 0 0 2-2V8l4 4V6.5zM7.42 8H16l-8.58 8.58V8zM2 6l2 2V5h10.17L12 3H4a2 2 0 0 0-2 2v11l2 2V6z"/></svg>
            : <svg viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          }
        </button>
        <button onClick={hangUp} style={S.hangUpBtn} title="End call">
          <HangUpIcon size={28} />
        </button>
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  incomingCard: {
    background: '#1a1a2e', borderRadius: 20, padding: '36px 48px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  },
  avatar: {
    width: 80, height: 80, borderRadius: '50%', overflow: 'hidden',
    background: '#4a9eff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarInitial: { color: '#fff', fontSize: 32, fontWeight: 700 },
  callerName: { color: '#fff', fontSize: 22, fontWeight: 600, margin: 0 },
  callerSub: { color: '#aaa', fontSize: 14, margin: '4px 0 20px' },
  callActions: { display: 'flex', gap: 24 },
  muteBtnSmall: {
    border: 'none', borderRadius: 20, padding: '6px 14px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    color: 'white', fontSize: 12, marginBottom: 8,
  },
  answerBtnRed: {
    width: 68, height: 68, borderRadius: '50%', background: '#ff3b30',
    border: 'none', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(255,59,48,0.5)',
  },
  answerBtnGreen: {
    width: 68, height: 68, borderRadius: '50%', background: '#34c759',
    border: 'none', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(52,199,89,0.5)',
  },
  // Minimized bubble — rounded rectangle showing both streams
  miniBubble: {
    position: 'fixed', zIndex: 9998,
    width: MINI_W, height: MINI_H, borderRadius: 14,
    overflow: 'hidden', cursor: 'grab',
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
    userSelect: 'none', background: '#111',
    border: '2px solid rgba(255,255,255,0.15)',
    touchAction: 'none', // prevent browser page-zoom on pinch
  },
  miniRemote: {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%', objectFit: 'cover',
  },
  miniLocal: {
    position: 'absolute', bottom: 6, right: 6,
    width: 144, height: 108, objectFit: 'cover',
    borderRadius: 6, border: '1.5px solid rgba(255,255,255,0.5)',
    background: '#333',
  },
  miniHangup: {
    position: 'absolute', top: 6, right: 6,
    width: 24, height: 24, borderRadius: '50%',
    background: '#ff3b30', border: 'none', color: 'white',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fullscreen: { position: 'fixed', inset: 0, zIndex: 9998, background: '#000', touchAction: 'none' },
  warningBanner: {
    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
    zIndex: 10000, background: 'rgba(255, 159, 10, 0.95)', color: '#1a1a1a',
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    maxWidth: '90%', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  remoteVideo: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', touchAction: 'none' },
  localVideo: {
    position: 'fixed', zIndex: 9999,
    width: PIP_W, height: PIP_H, objectFit: 'cover',
    borderRadius: 12, border: '2px solid rgba(255,255,255,0.3)',
    touchAction: 'none',
    background: '#111', userSelect: 'none',
    transition: 'left 0.25s ease, top 0.1s ease',
  },
  topBar: {
    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(rgba(0,0,0,0.55), transparent)',
  },
  callLabel: { color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600 },
  topBtn: {
    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
    padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  },
  missedBadge: {
    background: '#ff3b30', border: 'none', borderRadius: 16,
    padding: '4px 10px', cursor: 'pointer', color: 'white', fontSize: 13, fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 4,
    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
    animation: 'missedPulse 1.6s ease-in-out infinite',
  },
  miniMissedBadge: {
    position: 'absolute', top: 6, left: 6,
    background: '#ff3b30', color: 'white',
    minWidth: 20, height: 20, padding: '0 6px',
    borderRadius: 10, fontSize: 11, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
    cursor: 'pointer',
  },
  controls: {
    position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 18,
    padding: '18px 0 24px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
  },
  ctrlBtn: {
    border: 'none', borderRadius: '50%', width: 56, height: 56,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  audioMenu: {
    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
    marginBottom: 8, background: '#222', borderRadius: 10, overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)', minWidth: 160, zIndex: 3,
  },
  audioMenuItem: {
    display: 'block', width: '100%', padding: '10px 14px',
    border: 'none', background: 'transparent', color: 'white',
    cursor: 'pointer', fontSize: 13, textAlign: 'left',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  hangUpBtn: {
    border: 'none', borderRadius: '50%', width: 68, height: 68,
    background: '#ff3b30', cursor: 'pointer', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(255,59,48,0.5)',
  },
};
